import React, { memo, forwardRef, useImperativeHandle, useState } from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import { editImpactReport, fetchHarvestSummarys } from 'api/reports';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { ImpactReport } from 'types/report';
import { RefType, EditImpactReportProps } from './EditImpactReport.props';

import EditImpactReportView from './EditImpactReport.view';

const Container = forwardRef<RefType, EditImpactReportProps>((props, ref) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [report, setReport] = useState<ImpactReport | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_report) => {
        setReport(_report);
        onOpen();
      },
    }),
    [],
  );

  const queryFn = () =>
    fetchHarvestSummarys({
      page: 1,
      limit: 100,
    });

  const options = {
    keepPreviousData: true,
  };

  const { data, isLoading: isLoadingReports } = useQuery(
    'fetch-inventory-reports-edit',
    queryFn,
    options,
  );

  const { mutate, isLoading } = useMutation(editImpactReport, {
    onSuccess: () => {
      toast({
        status: 'success',
        title: 'Success',
        description: 'Impact Report Updated',
        isClosable: true,
        duration: 5000,
      });

      queryClient.invalidateQueries({
        queryKey: ['fetch-impact-reports'],
      });

      queryClient.invalidateQueries({
        queryKey: 'fetch-inventory-reports',
      });

      queryClient.invalidateQueries({
        queryKey: 'fetch-inventory-reports-edit',
      });

      onClose();
    },
  });

  const inventoryReports = data?.items || [];
  return (
    <EditImpactReportView
      report={report}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={(payload: any) => {
        const co2m = payload?.co2m.replace(/,/g, '') || 0;
        const cos = payload?.cos.replace(/,/g, '') || 0;
        const er = payload?.er.replace(/,/g, '') || 0;
        const wc = payload?.wc.replace(/,/g, '') || 0;
        const wd = payload?.wd.replace(/,/g, '') || 0;
        mutate({
          co2m: Number(co2m),
          cos: Number(cos),
          er: Number(er),
          wc: Number(wc),
          wd: Number(wd),
          name: payload?.name || '',
          id: payload?.id || '',
          startDate: new Date(payload?.startDate) as any,
          endDate: new Date(payload?.endDate) as any,
          month: Number(payload?.month || '1'),
          year: Number(payload?.year || '2023'),
          single_use_items: Number(payload?.single_use_items || '0'),
        });
      }}
      isLoadingReports={isLoadingReports}
      inventoryReports={inventoryReports}
    />
  );
});

const EditImpactReport = memo(Container);

export default EditImpactReport;
